export const GROUP_SESSIONS_GUIDE = `
# Group Sessions Guide

Group Sessions enable you to create personalized wellness experiences for teams, families, friends, or any group of people. This guide explains how to set up and manage groups effectively.

## What Are Group Sessions?

Group Sessions are **AI-generated wellness sessions designed for multiple people simultaneously**, incorporating:

- Shared group context (team challenges, family dynamics, shared goals)
- Individual participant details (names, roles, specific needs)
- Collective therapeutic content that addresses the group as a whole
- Unified experience that brings people together

### Use Cases

**Teams & Organizations:**
- Department team building and wellness
- Project team stress management
- Leadership group mindfulness
- Company-wide wellness initiatives

**Families:**
- Family meditation and bonding
- Sibling conflict resolution
- Parenting stress relief
- Multi-generational connection

**Friends & Social Groups:**
- Book club mindfulness
- Running club mental preparation
- Support groups (grief, recovery, parenting)
- Hobby groups (yoga class, art group)

**Couples:**
- Relationship strengthening
- Conflict de-escalation
- Shared stress management
- Connection rebuilding

## Creating Your First Group

### Step 1: Navigate to Groups

1. Click **"Groups"** in the main navigation
2. Click **"Create New Group"** button
3. You'll be taken to the Group Setup form

### Step 2: Choose Group Type

Select the type that best matches your group:

- **Team/Department** - Work teams, professional groups
- **Family** - Immediate or extended family
- **Friends** - Friend circles, social groups
- **Project Team** - Temporary project-based teams
- **Support Group** - Mutual support groups
- **Other** - Custom group types

**Why it matters:** Group type influences the tone and therapeutic approach AI uses.

### Step 3: Basic Group Information

**Group Name** (Required)
- Choose a descriptive name: "Marketing Team", "Smith Family", "Running Buddies"
- This name appears in session titles and group selection

**Group Description** (Optional but recommended)
- Describe the group's purpose or dynamic
- Example: "A tight-knit product team working on our Q2 launch, dealing with high pressure and tight deadlines"
- Example: "A blended family navigating new dynamics after my remarriage"

### Step 4: Add Participants

For each participant, provide:

**Name** (Required)
- First name or nickname the group uses
- Used throughout the session to address individuals

**Role** (Optional)
- Team role, family position, or function in the group
- Examples: "Product Manager", "Eldest child", "Group organizer", "Team lead"

**Individual Context** (Optional but powerful)
- Brief description of this person's specific situation or needs
- Example: "Struggling with work-life balance, new parent, tends to people-please"
- Example: "Recently diagnosed with anxiety, hesitant to speak up in groups"

**Why participant details matter:**
AI can reference specific people by name and address their individual needs within the collective session, creating powerful moments of personal recognition.

### Step 5: Group Context & Goals

**Group Biography** (Recommended - 1000 characters)
Describe the group's:
- Shared challenges or stressors
- Common goals or mission
- Group dynamics (cohesive? Fractured? New? Established?)
- Recent events affecting the group
- What the group is working toward

**Example - Work Team:**
> "We're the marketing team at a startup in hypergrowth mode. 6 months ago we were 4 people; now we're 12. The pace is brutal - 60-hour weeks are normal. Leadership keeps changing priorities. Half the team is new and struggling to integrate. We care about each other but we're too busy to connect. Burnout is real. Our goal is to hit Q3 targets without losing our humanity or our best people."

**Example - Family:**
> "Blended family formed 18 months ago when Tom and I married. His two kids (ages 10 and 13) spend weekends with us; my son (age 8) lives with us full-time. Transitions are rocky. Kids feel loyalty conflicts. Tom and I are trying to create unity but often feel like we're failing. My son resents sharing me. Older stepkids testing boundaries. Despite challenges, there's love here - we just need tools to navigate the complexity."

**Group Challenge** (Optional)
The specific challenge the group is currently facing:
- "We're missing our Q2 revenue target and tensions are high"
- "Recent family conflict over holiday plans has everyone on edge"
- "Two team members had a public argument and it's affecting the whole team"

**Group Goal** (Optional)
What the group is working toward:
- "Launch the product successfully while maintaining team cohesion"
- "Create a family culture where all kids feel equally valued"
- "Improve communication so conflicts don't escalate"

### Step 6: Save Your Group

Click **"Create Group"** to save. Your group is now ready for session generation!

## Managing Groups

### Editing Group Information

1. Navigate to **Groups**
2. Click on the group you want to edit
3. Click **"Edit Group"**
4. Update any information
5. Click **"Save Changes"**

**When to update:**
- Participants change (someone joins or leaves)
- Group dynamics shift significantly
- New challenges or goals emerge
- After major group events

### Adding/Removing Participants

**To Add:**
1. Edit the group
2. Scroll to Participants section
3. Click **"Add Participant"**
4. Fill in their details
5. Save

**To Remove:**
1. Edit the group
2. Find the participant
3. Click **"Remove"** (trash icon)
4. Save

### Archiving Groups

When a group is no longer active:

1. Go to Groups page
2. Click on the group
3. Click **"Archive Group"**
4. Confirm

**Archived groups:**
- Don't appear in your active groups list
- Can be viewed in "Archived Groups" section
- Previous sessions remain accessible
- Can be unarchived if group reforms

## Generating Group Sessions

### Step 1: Select Your Group

1. Choose a wellness category (Meditation, Anxiety, Sleep, etc.)
2. Click **"Create Group Session"**
3. Select the group from the dropdown
4. Group details will populate automatically

### Step 2: Session-Specific Context

Answer the questionnaire with group-specific responses:

**"What's the group dealing with right now?"**
- Be specific about current challenges
- Reference recent events or stressors
- Describe the emotional climate

**"What does the group need from this session?"**
- Stress relief? Team bonding? Conflict resolution? Energy boost?
- Connect to group goals
- Consider what would serve all participants

**"Any specific participants to focus on?"** (Optional)
- Highlight individuals who need extra support
- Note anyone going through something significant
- AI will weave their needs into the session while addressing the group

### Step 3: Session Preferences

**Duration:**
- **5-7 minutes** - Standard group sessions
- **10-15 minutes** - Deep team building or family sessions

**Voice & Music:**
- Choose voice and background music the group would respond to
- Consider: professional team = calming authoritative voice; family = warm friendly voice

### Step 4: Generate & Share

1. Click **"Generate Session"**
2. Wait 45-90 seconds (group sessions take longer)
3. **Preview the session** before sharing
4. **Share with group members** via:
   - Email (enter participant emails)
   - Direct link (copy and send)
   - Download for in-person playback

## Group Session Best Practices

### 1. Set the Context Clearly

The more detailed your group biography and challenge description, the more the AI can address specific group dynamics.

**Vague:** "Team is stressed"
**Specific:** "Team is 2 weeks from product launch, 3 critical bugs just discovered, developer and designer are blaming each other, PM (Sarah) is mediating but exhausted, morale is tanking"

### 2. Name Participants Strategically

Using real names creates powerful moments of personal recognition within a group session:

> "Sarah, you've been holding this team together. Mark, your frustration is valid. Jessica, your quiet leadership hasn't gone unnoticed."

### 3. Update Groups Regularly

Group dynamics evolve. Update group info:
- After major events (launches, conflicts, celebrations)
- When participants join or leave
- Every 1-2 months for active groups
- When sessions start feeling less relevant

### 4. Right-Size Your Groups

**Ideal group sizes:**
- **2-4 people** - AI can address each person individually
- **5-8 people** - AI can reference most people, focus on key members
- **9-15 people** - AI addresses group collectively, highlights a few individuals
- **16+ people** - Consider breaking into smaller groups for deeper personalization

### 5. Use Group Sessions for Key Moments

**Team/Organization:**
- Post-mortems after difficult projects
- Team retreats or off-sites
- After organizational changes
- During high-pressure periods
- Quarterly team reflection

**Family:**
- After family conflicts
- Before big transitions (moves, school changes)
- During stressful periods (holidays, illness)
- Regular family mindfulness practice
- Celebrating victories together

### 6. Combine with Individual Sessions

Group sessions are powerful for collective experiences, but participants may also need individual sessions for personal challenges. Encourage group members to use personal sessions alongside group ones.

### 7. Create Listening Rituals

**For Teams:**
- Monday morning team centering
- Friday wind-down sessions
- Pre-sprint planning mindfulness
- Post-conflict cool-down sessions

**For Families:**
- Sunday evening family meditation
- Before family meetings
- Morning school prep mindfulness
- Bedtime wind-down together

## Group Limits by Subscription

Your subscription tier determines how many groups you can create:

| Subscription Tier | Max Groups |
|------------------|------------|
| Free (Credits Only) | 1 group |
| Individual Basic | 4 groups |
| Individual Pro | 10 groups |
| Practitioner Basic | 4 groups |
| Practitioner Pro | Unlimited |

**To increase group limits:**
1. Navigate to **Credits → Upgrade Subscription**
2. Choose a higher tier
3. Your group limit increases immediately

**What happens if you exceed limits:**
- You cannot create additional groups until you upgrade
- Existing groups remain active and functional
- Sessions for existing groups continue working
- Archive unused groups to create space for new ones

## Group Session Credit Costs

Group sessions consume credits based on duration, just like personal sessions:

**Session Credit Costs:**
- 3-minute session = 10 credits
- 5-minute session = 17 credits
- 7-minute session = 23 credits
- 10-minute session = 33 credits

**Important:** One session credit charge covers the entire group. You don't pay per participant - you pay per session length.

### Example Cost Scenarios

**Scenario 1: Small team weekly sessions**
- 5-person team
- 7-minute sessions weekly
- 23 credits x 4 weeks = 92 credits/month

**Scenario 2: Family monthly sessions**
- 4-person family
- 10-minute sessions monthly
- 33 credits/month

**Scenario 3: Project team intensive period**
- 8-person project team
- Daily 5-minute sessions for 2 weeks (10 business days)
- 17 credits x 10 days = 170 credits for the intensive period

## Advanced Group Features

### Sub-Groups and Roles

For complex groups (large organizations, extended families), use roles and descriptions to create sub-groups:

**Example - Department:**
- Participant: "Sarah" | Role: "Team Lead"
- Participant: "Mark" | Role: "Senior Engineer - Backend Squad"
- Participant: "Jessica" | Role: "Senior Engineer - Frontend Squad"
- Participant: "Tom" | Role: "Junior Engineer - Backend Squad"

AI recognizes sub-structure and can reference squad dynamics.

### Temporary vs Ongoing Groups

**Ongoing Groups:**
- Update biography and challenges regularly
- Generate sessions as needed
- Build session history over time
- Track group evolution

**Temporary Groups (e.g., Project Teams):**
- Create group at project start
- Generate sessions during key moments
- Archive group when project ends
- Session history preserved

### Multi-Purpose Groups

One group can be used for different session types:

- Monday: **Motivation** session to start the week
- Wednesday: **Anxiety Relief** during crunch time
- Friday: **Celebration & Reflection** after shipping

Same group, different category/context based on current needs.

## Troubleshooting Group Sessions

### "Session doesn't mention all participants"

**Cause:** Too many participants (10+) or insufficient individual descriptions

**Fix:**
- Reduce group size or split into smaller groups
- Add individual context for key participants
- In questionnaire, mention who needs focus

### "Session feels too generic"

**Cause:** Group biography too vague or missing

**Fix:**
- Add detailed group biography (aim for 500+ words)
- Describe specific challenges and dynamics
- Include recent events and emotional climate
- Update group info regularly

### "Wrong tone for my group"

**Cause:** Group type selection doesn't match actual group

**Fix:**
- Edit group and change type (Family vs Team vs Friends changes tone significantly)
- Add context about desired tone in group biography
- Try different categories (Meditation vs ZEN AF = very different tone)

### "Participants not feeling personally addressed"

**Cause:** Missing individual context for participants

**Fix:**
- Edit group and add individual descriptions for each participant
- Include their specific challenges, personalities, or needs
- Use questionnaire to highlight who needs attention in this specific session

## Privacy & Sharing

### Group Information Security

- Group details are **private to your account**
- Only you can see group biographies and participant info
- Generated sessions can be shared, but not group configuration

### Sharing Sessions with Group Members

When you share a session:

**What they receive:**
- Direct link to the audio session
- Session title and duration
- Ability to listen (no account required)

**What they DON'T see:**
- Your account information
- Other groups or sessions
- Group biography or participant details
- Your personal biography

### Participant Consent

**Best Practice:**
Before creating a group and generating sessions:

1. **Inform participants** you're creating wellness sessions for the group
2. **Explain what info you'll include** (names, roles, general context)
3. **Get verbal/written consent** especially for workplace groups
4. **Respect privacy** - don't include deeply personal info about others without permission
5. **Allow opt-out** - participants can decline to be named or included

## Group Session Examples

### Example 1: Stressed Marketing Team

**Group Type:** Team/Department
**Participants:**
- Sarah (Team Lead, overwhelmed with stakeholder management)
- Mark (Designer, frustrated with constant revisions)
- Jessica (Copywriter, imposter syndrome)
- Tom (Analyst, quiet but burned out)

**Group Biography:**
> "4-person marketing team at Series B startup. Responsible for rebrand launching in 3 weeks. CEO keeps changing direction. Sarah mediates but is exhausted. Mark and CEO clashing over design vision. Jessica doubting her work after harsh feedback. Tom working nights and weekends on analytics, burning out silently. We used to grab lunch together; now we barely talk beyond Slack. We need to remember we're on the same side."

**Session Generated:**
> *7-minute Anxiety Relief session*
> "Sarah, Mark, Jessica, Tom. You four. Right now. This rebrand is trying to break you, but here's what it doesn't know: you've shipped impossible things before. Sarah, you held this team together during the last crisis. Mark, your design won the pitch that funded this company. Jessica, clients specifically request you because your words *move* people. Tom, your data caught the bug that would've tanked the launch last year. This tension you're feeling? That's not weakness. That's excellence under pressure..."

### Example 2: Blended Family

**Group Type:** Family
**Participants:**
- Tom (Stepdad, trying to create unity)
- Me (Mom, feeling torn between kids)
- Mia (Stepdaughter age 13, testing boundaries)
- Liam (Stepson age 10, withdrawn)
- Jake (My son age 8, resentful of changes)

**Group Biography:**
> "Blended family, 18 months post-marriage. Tom's kids (Mia & Liam) visit every other weekend. My son Jake lives with us full-time. Transitions are tough - kids act out during handoffs. Mia pushes boundaries, Tom struggles with discipline consistency, I'm caught in middle. Liam withdraws, I worry about him but don't want to overstep. Jake resents sharing me, says mean things to stepkids. Tom and I argue about parenting approaches after kids in bed. Despite mess, moments of connection happen - board games, inside jokes forming. We want to be a real family, not just people coexisting."

**Session Generated:**
> *10-minute Family Meditation*
> "Tom, Sarah, Mia, Liam, Jake. Five people learning to be a family. Not pretending it's easy. Not pretending you're the Brady Bunch. Mia, your loyalty to your mom is beautiful - and you can love her AND let Tom care about you. Liam, your quietness isn't a problem; you're processing at your own pace. Jake, buddy, sharing your mom is hard, but look: she loves you exactly the same as before. Tom, you're doing the impossible task of loving kids who aren't sure they want to be loved yet. Sarah, stop trying to make everyone happy at once. This is messy, and messy is normal..."

---

**Ready to create group sessions?** Set up your first group and experience collective wellness.

Need help? Browse other guides in the Help Center or contact support at support@voicethera.com
`;
